/** Minimal metrics shape shared between ProjectKpiMetrics and DerivativeKpiMetrics */
interface MetricsWithEntryValues {
  latest_entry_value?: number | null;
  second_latest_entry_value?: number | null;
  aggregated_latest_value?: number | null;
  aggregated_previous_value?: number | null;
}

/** Get aggregated value (sum/avg of subprojects) or fallback to single entry value */
export const getAggregatedValue = (
  metrics: MetricsWithEntryValues | undefined,
): number =>
  metrics?.aggregated_latest_value ?? metrics?.latest_entry_value ?? 0;

/** Get previous aggregated value (before the latest entry was added) */
export const getAggregatedPreviousValue = (
  metrics: MetricsWithEntryValues | undefined,
): number =>
  metrics?.aggregated_previous_value ?? metrics?.second_latest_entry_value ?? 0;

/** Calculate the percentage change between two values */
export const calculateChangePercentage = (
  current: number,
  previous: number,
): number => (previous !== 0 ? ((current - previous) / previous) * 100 : 0);

interface KpiMetricsSummary {
  currentValue: number;
  previousValue: number;
  changePercentage: number;
}

/** Compute current, previous value and change percentage from KPI metrics in one call. */
export function getKpiMetricsSummary(
  metrics: MetricsWithEntryValues | undefined,
): KpiMetricsSummary {
  const currentValue = getAggregatedValue(metrics);
  const previousValue = getAggregatedPreviousValue(metrics);
  const changePercentage = calculateChangePercentage(
    currentValue,
    previousValue,
  );
  return { currentValue, previousValue, changePercentage };
}
